import { useMemo } from 'react';
import { useQuery, gql } from '@apollo/client';
import { GENDER_OPTIONS } from "../../../../models/product";
import { FILTER_KEYS, SEARCH_SIZE_TYPE_GROUPS } from "../../../../constants/constants";

const GET_SEARCH_SIZE_FILTER_FOR_TAGS = gql`
  query GetSearchSizeFilter {
    getSearchSizeFilter {
      size_type_id
      size_values {
        id
        name
        sort_order
      }
    }
  }
`;

// 카테고리 데이터를 기반으로 서브카테고리 ID를 키로 하는 맵을 생성합니다.
// 이 맵은 서브카테고리 이름을 효율적으로 찾는 데 사용됩니다. (O(1) 시간 복잡도)
const createSubcategoryMap = (categories) => {
    const map = new Map();
    if (categories) {
        categories.forEach(category => {
            if (category.subcategories) {
                category.subcategories.forEach(sub => {
                    map.set(String(sub.id), sub.name);
                });
            }
        });
    }
    return map;
};

/**
 * 필터 객체를 UI에 표시될 태그 배열로 변환하는 커스텀 훅입니다.
 * @param {object} filters - 현재 적용된 필터 객체
 * @param {Array} categories - 전체 카테고리 목록
 * @returns {Array} UI에 표시될 필터 태그 배열
 */
const useFilterTags = (filters, categories) => {
    // 카테고리 목록이 변경될 때만 서브카테고리 맵을 다시 생성합니다.
    const subcategoryMap = useMemo(() => createSubcategoryMap(categories), [categories]);

    const hasSizeFilter = (filters?.sizeValueIds?.length ?? 0) > 0;
    const { data: sizeFilterData } = useQuery(GET_SEARCH_SIZE_FILTER_FOR_TAGS, {
        fetchPolicy: 'cache-first',
        skip: !hasSizeFilter,
    });

    // size_value_id -> { typeId, name, sortOrder } 매핑. 사이즈 칩을 type 단위로 묶고 라벨에 사이즈명을 노출하기 위해 사용.
    const sizeValueMeta = useMemo(() => {
        const map = new Map();
        (sizeFilterData?.getSearchSizeFilter || []).forEach((group) => {
            (group.size_values || []).forEach((v) => {
                map.set(Number(v.id), {
                    typeId: Number(group.size_type_id),
                    name: v.name,
                    sortOrder: v.sort_order ?? 0,
                });
            });
        });
        return map;
    }, [sizeFilterData]);

    // 필터나 서브카테고리 맵이 변경될 때만 태그 목록을 다시 계산합니다.
    return useMemo(() => {
        const tags = [];

        // 품절 상품 제외 필터
        if (filters.isSelling) {
            tags.push({ type: 'isSelling', label: '품절 상품 제외', value: 'isSelling' });
        }

        // 새상품만 보기 필터
        if (filters.condition === 'NEW') {
            tags.push({ type: 'condition', label: '새상품만 보기', value: 'NEW' });
        }

        // 성별 필터
        if (filters.gender) {
            const genderOption = GENDER_OPTIONS.find(opt => opt.value === filters.gender);
            tags.push({ type: 'gender', label: (genderOption && genderOption.label) || filters.gender, value: filters.gender });
        }

        // 서브카테고리 필터
        if (filters.subcategoryIds) {
            filters.subcategoryIds.forEach(id => {
                const name = subcategoryMap.get(String(id));
                if (name) {
                    tags.push({ type: 'subcategoryIds', label: name, value: id });
                }
            });
        }

        // 브랜드 필터
        if (filters.brands) {
            filters.brands.forEach(brand => {
                const brandName = typeof brand === 'string' ? brand : brand.name;
                tags.push({ type: 'brands', label: brandName, value: brand });
            });
        }

        // 가격 필터
        if (filters.priceMin != null || filters.priceMax != null) {
            const minPrice = filters.priceMin || 0;
            const maxPrice = filters.priceMax || 0;
            const label = `${minPrice.toLocaleString()}원 ~ ${maxPrice === 0 ? '제한 없음' : maxPrice.toLocaleString() + '원'}`;
            tags.push({ type: 'price', label, value: 'price' });
        }

        // 사이즈 필터 — size_type 그룹 단위 칩, 라벨에 선택된 사이즈명을 노출.
        if (filters.sizeValueIds && filters.sizeValueIds.length > 0 && sizeValueMeta.size > 0) {
            const itemsByType = new Map();
            filters.sizeValueIds.forEach((id) => {
                const meta = sizeValueMeta.get(Number(id));
                if (!meta) return;
                if (!itemsByType.has(meta.typeId)) itemsByType.set(meta.typeId, []);
                itemsByType.get(meta.typeId).push({ id: Number(id), name: meta.name, sortOrder: meta.sortOrder });
            });
            SEARCH_SIZE_TYPE_GROUPS.forEach(({ id: typeId, label }) => {
                const items = itemsByType.get(typeId);
                if (!items || items.length === 0) return;
                const sorted = [...items].sort((a, b) => a.sortOrder - b.sortOrder);
                const names = sorted.map((item) => item.name).join(', ');
                tags.push({
                    type: FILTER_KEYS.SIZE_VALUE_IDS,
                    label: `${label} ${names}`,
                    value: { id: typeId, valueIds: sorted.map((item) => item.id) },
                });
            });
        }

        return tags;
    }, [filters, subcategoryMap, sizeValueMeta]);
};

export default useFilterTags;
